import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import { CII_RATINGS } from "@/features/voyagePlanner/fuelRequestParams"
import { nowUtc } from "@/utils/appNow"

dayjs.extend(utc)

export const PRIORITIES = ["time", "fuel", "warnings"]
export const PRIORITY_LABELS = { time: "Time", fuel: "Fuel", warnings: "Warnings" }
const HEADLINE_LABELS = {
  time: "Time saved",
  fuel: "Fuel saved",
  warnings: "Warnings avoided",
}
const LOSS_LABELS = {
  time: "Time lost",
  fuel: "Extra fuel",
  warnings: "Warnings added",
}

// Summary fields that count as warnings (speed limits are infos).
export const WARNING_FIELDS = [
  ["weatherWarnings", "weather"],
  ["rnwWarnings", "RNW"],
  ["hraWarnings", "high risk"],
  ["jwlaWarnings", "war risk"],
  ["customAreaWarnings", "operational risk"],
  ["ecaWarnings", "ECA"],
]

const MINUS = "−"
const THIN_SPACE = " "

const finite = (v) => (typeof v === "number" && Number.isFinite(v) ? v : null)

// "3 380.8": fixed decimals, thin-space thousands separator, true minus.
export function formatNumber(value, digits = 0) {
  const n = finite(value)
  if (n == null) return "—"
  const [int, frac] = Math.abs(n).toFixed(digits).split(".")
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, THIN_SPACE)
  return `${n < 0 ? MINUS : ""}${grouped}${frac ? `.${frac}` : ""}`
}

function signed(value, digits, unit) {
  const n = finite(value)
  if (n == null) return null
  if (Number(n.toFixed(digits)) === 0) return "±0"
  return `${n > 0 ? "+" : MINUS}${formatNumber(Math.abs(n), digits)}${unit ? ` ${unit}` : ""}`
}

// Unsigned "5d 5h 5m" / "8h 58m" / "45m"; zero parts are dropped.
export function formatHours(hours) {
  const total = Math.round(Math.abs(hours) * 60)
  const d = Math.floor(total / 1440)
  const h = Math.floor((total % 1440) / 60)
  const m = total % 60
  const parts = []
  if (d) parts.push(`${d}d`)
  if (h) parts.push(`${h}h`)
  if (m) parts.push(`${m}m`)
  return parts.length ? parts.join(" ") : "0h"
}

function signedHours(hours) {
  const n = finite(hours)
  if (n == null) return null
  if (Math.abs(n) < 1 / 120) return "±0"
  return `${n > 0 ? "+" : MINUS}${formatHours(n)}`
}

export function totalWarnings(summary) {
  let total = null
  for (const [field] of WARNING_FIELDS) {
    const n = finite(Number(summary?.[field]))
    if (summary?.[field] == null || n == null) continue
    total = (total ?? 0) + n
  }
  return total
}

export function warningBreakdown(summary) {
  return WARNING_FIELDS.filter(([field]) => Number(summary?.[field]) > 0).map(
    ([field, label]) => `${summary[field]} ${label}`,
  )
}

export function cardKind(summary) {
  if (summary?.isSuggested) return "unsaved"
  if (summary?.isActive) return "active"
  const state = String(summary?.state ?? "active").toLowerCase()
  return state === "suggested" || state === "archived" ? state : "active"
}

function pillLabel(kind, optimisedFor) {
  const objective = optimisedFor ? optimisedFor.toUpperCase() : null
  if (kind === "active") return "ACTIVE"
  if (kind === "archived") return "ARCHIVED"
  if (kind === "unsaved") return `DRAFT · ${objective ?? "MANUAL"}`
  return objective ? `SUGGESTED · ${objective}` : "SUGGESTED"
}

const delta = (value, base) => {
  const a = finite(value)
  const b = finite(base)
  return a == null || b == null ? null : a - b
}

// Delta chip: lower is better for every compared quantity.
function chip(label, diff, digits = 6) {
  if (label == null) return null
  const d = finite(diff)
  if (d == null) return null
  const rounded = Number(d.toFixed(digits))
  const tone = rounded === 0 ? "neutral" : rounded < 0 ? "better" : "worse"
  return { label, tone }
}

const hoursFromDays = (days) => (finite(days) == null ? null : days * 24)

function etaHoursDelta(summary, baseline) {
  if (!summary?.etaUtc || !baseline?.etaUtc) return null
  return dayjs.utc(summary.etaUtc).diff(dayjs.utc(baseline.etaUtc), "hour", true)
}

// Under way: the baseline already left, so candidates start from the vessel's
// position and only ETA and remaining distance are comparable.
function underWay(baseline, now) {
  const departure = baseline?.departureUtc ? dayjs.utc(baseline.departureUtc) : null
  return Boolean(departure?.isValid() && now.isAfter(departure))
}

const distanceOf = (summary, live) => (live ? summary.remainingDistanceNm : summary.distanceNm)

function fuelValue(summary) {
  const tons = finite(summary.fuelTons)
  if (tons == null) return "—"
  const cost = finite(summary.fuelCostUsd)
  return `${formatNumber(tons, 1)} t${cost != null ? ` · ${formatNumber(cost, 0)} $` : ""}`
}

function priceBasis(prices) {
  if (finite(prices?.usdPerTon) == null) return null
  const eca = finite(prices.ecaUsdPerTon)
  return `${formatNumber(prices.usdPerTon)}${eca == null ? "" : ` / ${formatNumber(eca)}`} $/t`
}

// Fuel cost rests on the request's bunker prices; a differing baseline basis
// makes the cost delta a price effect, so the tooltip names both.
export function fuelTooltip(summary, baseline) {
  if (finite(summary?.fuelCostUsd) == null) return null
  const basis = priceBasis(summary.bunkerPrices)
  if (!basis) return null
  const baselineBasis = baseline && baseline !== summary ? priceBasis(baseline.bunkerPrices) : null
  return baselineBasis && baselineBasis !== basis
    ? `Price basis ${basis} · baseline ${baselineBasis}`
    : `Price basis ${basis}`
}

function buildRows(summary, baseline, showCii, live) {
  const isBaseline = !baseline || summary === baseline
  const withChip = (row, c) => (isBaseline ? row : { ...row, delta: c })
  const fuelDelta = delta(summary.fuelTons, baseline?.fuelTons)
  const costDelta = delta(summary.fuelCostUsd, baseline?.fuelCostUsd)
  const warnings = totalWarnings(summary)
  const warningsDelta = delta(warnings, totalWarnings(baseline))
  const eta = etaHoursDelta(summary, baseline)
  const rows = [
    withChip(
      {
        key: "eta",
        label: "ETA (UTC)",
        value: summary.etaUtc ? dayjs.utc(summary.etaUtc).format("DD MMM HH:mm") : "—",
      },
      chip(signedHours(eta), eta == null ? null : Math.round(eta * 60), 0),
    ),
    withChip(
      {
        key: "distance",
        label: live ? "Distance to go" : "Distance",
        value:
          finite(distanceOf(summary, live)) == null
            ? "—"
            : `${formatNumber(distanceOf(summary, live), 1)} nm`,
      },
      (() => {
        const d = delta(distanceOf(summary, live), baseline && distanceOf(baseline, live))
        return chip(signed(d, 0, "nm"), d, 0)
      })(),
    ),
    withChip(
      { key: "fuel", label: "Fuel", value: fuelValue(summary), tooltip: fuelTooltip(summary, baseline) },
      costDelta != null
        ? chip(signed(Math.round(costDelta), 0, "$"), Math.round(costDelta), 0)
        : chip(signed(fuelDelta, 1, "t"), fuelDelta, 1),
    ),
    withChip(
      {
        key: "warnings",
        label: "Warnings",
        value: warnings == null ? "—" : warnings === 0 ? "none" : warningBreakdown(summary).join(" · "),
        tooltip: warningBreakdown(summary).join(", ") || null,
      },
      chip(signed(warningsDelta, 0), warningsDelta, 0),
    ),
  ]
  if (showCii) {
    const ri = CII_RATINGS.indexOf(summary.cii?.rating)
    const bi = CII_RATINGS.indexOf(baseline?.cii?.rating)
    const d = ri < 0 || bi < 0 ? null : ri - bi
    rows.push(
      withChip(
        {
          key: "cii",
          label: "CII",
          value: summary.cii?.rating
            ? `${summary.cii.rating}${summary.cii.year ? ` (${summary.cii.year})` : ""}`
            : "—",
          ciiRating: summary.cii?.rating ?? null,
        },
        chip(signed(d, 0), d, 0),
      ),
    )
  }
  return rows
}

// Positive savings vs. the baseline per dimension (null = not comparable).
// Time compares arrivals so it agrees with the ETA chip even when departures
// differ; duration is only the fallback when an ETA is missing.
function buildSavings(summary, baseline, live) {
  const durationHours = live
    ? null
    : delta(hoursFromDays(summary.durationDays), hoursFromDays(baseline.durationDays))
  const time = etaHoursDelta(summary, baseline) ?? durationHours
  const fuel = delta(summary.fuelTons, baseline.fuelTons)
  const warnings = delta(totalWarnings(summary), totalWarnings(baseline))
  const neg = (d) => (d == null ? null : -d)
  return { time: neg(time), fuel: neg(fuel), warnings: neg(warnings) }
}

// Headline for a saving (tone "good") or, when nothing is saved, a loss (tone "bad").
function headlineFor(dim, summary, baseline, savings, live) {
  const saving = savings[dim]
  const loss = saving < 0
  const tone = loss ? "bad" : "good"
  const label = (loss ? LOSS_LABELS : HEADLINE_LABELS)[dim]
  const amount = Math.abs(saving)
  if (dim === "time") {
    const distance = delta(distanceOf(summary, live), distanceOf(baseline, live))
    const nm = distance == null ? 0 : Math.round(Math.abs(distance))
    const sub = nm === 0 ? "" : `${formatNumber(nm)} nm ${distance < 0 ? "shorter" : "longer"}`
    return { dim, tone, label, value: formatHours(amount), sub }
  }
  if (dim === "fuel") {
    const cost = delta(summary.fuelCostUsd, baseline.fuelCostUsd)
    const sub = cost == null ? "" : `${formatNumber(Math.abs(cost), 0)} $`
    return { dim, tone, label, value: `${formatNumber(amount, 1)} t`, sub }
  }
  const changed = WARNING_FIELDS.map(([field, text]) => {
    const d = delta(summary[field], baseline[field])
    if (d == null || d === 0 || d < 0 === loss) return null
    return `${loss ? "+" : MINUS}${Math.abs(d)} ${text}`
  }).filter(Boolean)
  const n = Math.round(amount)
  return { dim, tone, label, value: `${n} warning${n === 1 ? "" : "s"}`, sub: changed.join(" · ") }
}

// Active route: where the voyage stands now instead of a saving vs. itself.
function progressHeadline(summary, now) {
  const eta = summary.etaUtc ? dayjs.utc(summary.etaUtc) : null
  if (!eta?.isValid()) return null
  const departure = summary.departureUtc ? dayjs.utc(summary.departureUtc) : null
  const distance = finite(summary.distanceNm)
  if (departure?.isValid() && now.isBefore(departure)) {
    const sub = distance == null ? "" : `${formatNumber(distance)} nm total`
    return { tone: "neutral", label: "Departs in", value: formatHours(departure.diff(now, "hour", true)), sub }
  }
  if (!now.isBefore(eta)) {
    return { tone: "neutral", label: "Arrived", value: eta.format("DD MMM HH:mm"), sub: "" }
  }
  const remainingDays = finite(summary.remainingDurationDays)
  const hours = remainingDays != null ? remainingDays * 24 : eta.diff(now, "hour", true)
  const nm = finite(summary.remainingDistanceNm)
  const sub = nm == null ? "" : `${formatNumber(nm)} nm to go`
  return { tone: "neutral", label: "Time to go", value: formatHours(hours), sub }
}

// Absolute values the other cards' bars are measured against.
function referenceValues(summary, live) {
  const days = finite(live ? summary.remainingDurationDays : summary.durationDays)
  const fuel = finite(summary.fuelTons)
  const warnings = totalWarnings(summary)
  return {
    time: days == null ? "—" : formatHours(days * 24),
    fuel: fuel == null ? "—" : `${formatNumber(fuel, 1)} t`,
    warnings: warnings == null ? "—" : String(warnings),
  }
}

// Planner objective → the dimension it maps to; "manual" has none.
export function priorityFromObjective(optimisedFor) {
  return optimisedFor === "time" || optimisedFor === "fuel" ? optimisedFor : null
}

// Default priority: objective of the first planned suggestion, else time.
export function defaultPriority(summaries) {
  const suggested = summaries.find((s) => cardKind(s) === "suggested")
  return priorityFromObjective(suggested?.optimisedFor) ?? "time"
}

export function buildOptionCards(summaries, priority = "time", now = nowUtc()) {
  const baseline = summaries.find((s) => s.isActive) ?? null
  const live = underWay(baseline, now)
  const showCii = summaries.some((s) => s.cii?.rating)
  const savingsById = new Map()
  if (baseline) {
    for (const s of summaries) {
      if (s !== baseline) savingsById.set(s.id, buildSavings(s, baseline, live))
    }
  }
  // Bars are signed: saving / largest |delta| per dimension, so a loss shows red.
  const maxDelta = Object.fromEntries(
    PRIORITIES.map((dim) => [
      dim,
      Math.max(0, ...[...savingsById.values()].map((s) => Math.abs(s[dim] ?? 0))),
    ]),
  )

  // Best: largest saving in the chosen priority among routes that can still
  // be chosen (archived ones are history), unique and positive.
  let bestId = null
  const candidates = summaries.filter(
    (s) => savingsById.has(s.id) && cardKind(s) !== "archived",
  )
  const bestSaving = Math.max(
    0,
    ...candidates.map((s) => savingsById.get(s.id)[priority] ?? 0),
  )
  if (bestSaving > 0) {
    const winners = candidates.filter(
      (s) => (savingsById.get(s.id)[priority] ?? 0) === bestSaving,
    )
    if (winners.length === 1) bestId = winners[0].id
  }

  const cards = summaries.map((summary) => {
    const kind = cardKind(summary)
    const isBaseline = summary === baseline
    const savings = savingsById.get(summary.id) ?? null
    const bars = Object.fromEntries(
      PRIORITIES.map((dim) => {
        const saving = savings?.[dim] ?? 0
        return [dim, maxDelta[dim] > 0 ? saving / maxDelta[dim] || 0 : 0]
      }),
    )
    let headline = isBaseline ? progressHeadline(summary, now) : null
    if (savings) {
      const objectiveDim = priorityFromObjective(summary.optimisedFor)
      const largest = (sign) =>
        PRIORITIES.reduce((best, d) => (sign * bars[d] > (best == null ? 0 : sign * bars[best]) ? d : best), null)
      const dim =
        objectiveDim && (savings[objectiveDim] ?? 0) > 0 ? objectiveDim : (largest(1) ?? largest(-1))
      headline = dim ? headlineFor(dim, summary, baseline, savings, live) : null
    }
    return {
      id: summary.id,
      name: summary.name,
      vesselName: summary.vesselName ?? null,
      routeId: summary.routeId ?? null,
      voyageId: summary.voyageId ?? null,
      vesselId: summary.vesselId ?? null,
      state: summary.state ?? null,
      isSuggested: Boolean(summary.isSuggested),
      computedAt: summary.computedAt ?? null,
      reference: isBaseline ? referenceValues(summary, live) : null,
      kind,
      isBaseline,
      comparedTo: isBaseline ? null : (baseline?.name ?? null),
      pill: { kind, label: pillLabel(kind, summary.optimisedFor) },
      rows: buildRows(summary, baseline, showCii, live),
      savings,
      bars,
      headline,
      best: summary.id === bestId,
      canApply: kind === "suggested" && summary.routeId != null,
    }
  })

  return { cards, hasBaseline: baseline != null, maxDelta }
}
